import {
  Campus,
  Intake,
  InventoryCourse,
  Maybe,
  StudyInNzDetailBanner_Swnz_GlobalSettingsCollectionFragment,
} from 'content-service'
import { format, parseISO } from 'date-fns'
import { createTagList } from 'tracking'
import { unique } from 'utils'
import { ROUTES } from '../../../constants'

const createMailtoHref = (campus: Campus | null | undefined, courseName: string) => {
  const { email: campusEmail, name: campusName } = campus || {}

  if (campusEmail && campusName && courseName) {
    return `mailto:${campusEmail}?subject=${courseName} at ${campusName}`
  }

  if (campusEmail) {
    return `mailto:${campusEmail}`
  }

  return ''
}

const formatStartDate = (startDate: string | undefined | null) => {
  if (startDate) {
    return format(parseISO(startDate), 'MMM yyyy')
  }

  return undefined
}

const findActiveIntakes = (
  intakes: Maybe<Maybe<Intake>[]> | undefined,
  campuses: Maybe<Maybe<Campus>[]> | undefined
) => {
  const activeCampuseIds = campuses?.map((campus) => campus?.id)
  const activeIntakes = intakes?.filter((intake) => activeCampuseIds?.includes(intake?.campus))
  return activeIntakes
}

const findFistValidIntakeCampus = (
  campusId: Maybe<string> | undefined,
  campuses: Maybe<Maybe<Campus>[]> | undefined
) => {
  return campuses?.find((campus) => campus && campus.id === campusId)
}

const determineShowHideDetailBanner = (
  firstValidIntake: Maybe<Intake> | undefined,
  intakes?: Maybe<Maybe<Intake>[]>
): boolean => {
  const isValidShowDetailBannerCase = 'in-new-zealand'
  const deliveryModeTag = firstValidIntake?.deliveryMode?.tag
    ? firstValidIntake.deliveryMode.tag
    : intakes?.[0]?.deliveryMode?.tag

  return deliveryModeTag === isValidShowDetailBannerCase
}

const formatDeliverModeMap: { [deliveryModeTag: string]: string } = Object.freeze({
  pathway: 'via Global pathways',
  'in-new-zealand': 'On-Campus',
  online: 'Online',
})

const determineModeOfStudyOptions = (
  intakes: Maybe<Maybe<Intake>[]> | undefined | null
): string => {
  const formatDeliverModeMap: { [deliveryModeTag: string]: string } = Object.freeze({
    pathway: 'via Global pathways',
    'in-new-zealand': 'On-Campus',
    online: 'Online',
  })

  const collatedDeliveryModes: string[] = []
  let sanitisedDeliveryModes = ''

  if (intakes?.length) {
    for (const item of intakes) {
      const deliveryModeTag = item?.deliveryMode?.tag

      if (deliveryModeTag && formatDeliverModeMap[deliveryModeTag]) {
        collatedDeliveryModes.push(formatDeliverModeMap[deliveryModeTag])
      }
    }
  }

  if (collatedDeliveryModes.length) {
    sanitisedDeliveryModes = [...new Set(collatedDeliveryModes)].join(', ')
  }

  return sanitisedDeliveryModes
}

const validateDuration = (
  duration: string | undefined | null,
  intakes: Maybe<Maybe<Intake>[]> | undefined | null = []
): string => {
  if (intakes && Array.isArray(intakes)) {
    return intakes?.find((item) => item?.duration?.length)?.duration || ''
  }

  return duration ?? ''
}

const validateCourseDetailProps = (courseDetail: InventoryCourse) => {
  const validCourseDetailProps = {
    id: courseDetail?.id || '',
    levelOfStudy: {
      name: courseDetail?.levelOfStudy?.name || '',
      tag: courseDetail?.levelOfStudy?.tag || '',
    },
    linkHref: courseDetail.provider?.id
      ? `${ROUTES.PROVIDER.DETAIL}/${courseDetail.provider.id}`
      : '',
    name: courseDetail?.name || '',
    logoUrl: courseDetail?.provider?.logoUrl || '',
    detailsHtmlString: courseDetail?.details || '',
    providerName: courseDetail.provider?.name || '',
    providerType: courseDetail.provider?.type?.tag || '',
    providerSubtype: courseDetail.provider?.subtype?.name || '',
    providerSubtypeList: createTagList(courseDetail.provider?.subtypeList),
    campusRegions: unique(
      courseDetail?.provider?.campuses?.map((campus) => campus?.region?.tag ?? '') ?? ['']
    ),
    modeOfStudyOptions: determineModeOfStudyOptions(courseDetail.intakes),
    summaryHtmlString: '', // summary field does't exist yet - field name may change
    subjectsOfStudy: createTagList(courseDetail?.subjectsOfStudy),
    areasOfStudy: createTagList(courseDetail?.areasOfStudy),
    deliveryModes: [
      ...new Set(courseDetail?.intakes?.map((intake) => intake?.deliveryMode?.tag ?? '') ?? ['']),
    ],
    entryRequirements: courseDetail?.entryRequirements || '',
  }

  return validCourseDetailProps
}

const validateDetailBannerProps = (
  detailBanner: StudyInNzDetailBanner_Swnz_GlobalSettingsCollectionFragment | undefined | null
) => {
  return {
    detailBannerCtaButtonItems: detailBanner?.items[0]?.callToActionCollection?.items || [],
    detailBannerRichText: detailBanner?.items[0]?.richText || null,
  }
}

export {
  determineShowHideDetailBanner,
  findActiveIntakes,
  findFistValidIntakeCampus,
  formatStartDate,
  validateCourseDetailProps,
  validateDetailBannerProps,
  createMailtoHref,
  determineModeOfStudyOptions,
  formatDeliverModeMap,
  validateDuration,
}
